import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, Search, MapPin, Lock, Loader2, Sparkles, TrendingUp, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChefCard } from "../components/ChefCard";

const SORTS = [["top", "Top rated", TrendingUp], ["newest", "Newest", Clock], ["recipes", "Most recipes", Sparkles]];

function FeaturedRow({ featured }) {
  const [tab, setTab] = useState("top");
  const list = featured?.[tab] || [];
  if (!featured || (!featured.top.length && !featured.newest.length)) return null;
  return (
    <section className="mt-10" data-testid="featured-chefs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-2xl sm:text-3xl font-black text-ink">Featured chefs</h2>
        <div className="flex gap-2">
          {[["top", "Top rated"], ["newest", "Newest members"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`featured-tab-${k}`}
              className={`rounded-full px-4 h-10 text-sm font-bold transition-colors ${tab === k ? "bg-ink text-white" : "bg-white border border-border text-ink-soft hover:border-coral"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x">
        {list.map((c) => <div key={c.id} className="w-[200px] sm:w-[220px] shrink-0 snap-start"><ChefCard chef={c} compact /></div>)}
      </div>
    </section>
  );
}

export default function ChefsHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "", city = params.get("city") || "", specialty = params.get("specialty") || "", sort = params.get("sort") || "top";
  const [input, setInput] = useState(q);
  const [meta, setMeta] = useState({ cities: [], specialties: [] });
  const [featured, setFeatured] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  const filtering = !!(q || city || specialty);
  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p); };
  const requireLogin = () => { if (!user) { navigate("/login"); return true; } return false; };

  useEffect(() => { api.get("/chefs/meta").then(({ data }) => setMeta(data)).catch(() => {}); api.get("/chefs/featured").then(({ data }) => setFeatured(data)).catch(() => {}); }, []);
  useEffect(() => { setInput(q); }, [q]);

  useEffect(() => {
    let active = true;
    setLoading(true); setGated(false);
    api.get("/chefs", { params: { q, city, specialty, sort, page: 1, limit: 12 } })
      .then(({ data }) => { if (!active) return; setChefs(data.chefs); setTotal(data.total); setHasMore(data.has_more); setPage(1); })
      .catch((err) => { if (!active) return; if (err.response?.status === 401) setGated(true); setChefs([]); setTotal(0); setHasMore(false); })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [q, city, specialty, sort, user]);

  const loadMore = async () => {
    const next = page + 1;
    const { data } = await api.get("/chefs", { params: { q, city, specialty, sort, page: next, limit: 12 } });
    setChefs((prev) => [...prev, ...data.chefs]); setHasMore(data.has_more); setPage(next);
  };

  const submitSearch = (e) => { e.preventDefault(); if (requireLogin()) return; setParam("q", input.trim()); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="chefs-hub">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
        <p className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-1.5 text-sm font-bold text-ink-soft shadow-soft"><ChefHat size={15} className="text-coral" /> Community chefs</p>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-tight text-ink leading-[1.05]">Meet the chefs behind the recipes</h1>
        <p className="mt-3 text-lg text-ink-soft font-medium">Home cooks and professionals sharing original recipes, stories and tips — from your city and beyond.</p>
      </motion.div>

      {/* Search + filters */}
      <div className="mt-8 rounded-3xl bg-white border border-border shadow-soft p-4 sm:p-5" data-testid="chefs-filters">
        <form onSubmit={submitSearch} className="flex gap-2">
          <div className="relative flex-1 min-w-0">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input value={input} onChange={(e) => setInput(e.target.value)} onFocus={() => !user && requireLogin()} data-testid="chefs-search-input"
              placeholder="Search chefs by name, city, specialty or dish…"
              className="w-full h-13 h-[52px] rounded-full border border-border bg-cream pl-11 pr-4 text-base outline-none focus:border-coral focus:ring-4 focus:ring-coral/10 font-medium" />
          </div>
          <button type="submit" data-testid="chefs-search-submit" className="h-[52px] px-6 rounded-full bg-coral hover:bg-coral-hover text-white font-bold transition-colors">Search</button>
        </form>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-coral pointer-events-none" />
            <select value={city} onChange={(e) => { if (requireLogin()) return; setParam("city", e.target.value); }} data-testid="chefs-city-filter"
              className="h-11 pl-8 pr-8 rounded-full border border-border bg-white text-sm font-bold text-ink outline-none focus:border-coral appearance-none">
              <option value="">All cities</option>
              {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <select value={specialty} onChange={(e) => { if (requireLogin()) return; setParam("specialty", e.target.value); }} data-testid="chefs-specialty-filter"
            className="h-11 px-4 rounded-full border border-border bg-white text-sm font-bold text-ink outline-none focus:border-coral appearance-none">
            <option value="">All specialties</option>
            {meta.specialties.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="ml-auto flex gap-1.5">
            {SORTS.map(([k, l, Icon]) => (
              <button key={k} onClick={() => setParam("sort", k)} data-testid={`chefs-sort-${k}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-11 text-sm font-bold transition-colors ${sort === k ? "bg-ink text-white" : "bg-white border border-border text-ink-soft hover:border-coral"}`}>
                <Icon size={14} /> <span className="hidden sm:inline">{l}</span>
              </button>
            ))}
          </div>
        </div>
        {!user && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-ink-soft" data-testid="chefs-guest-note">
            <Lock size={12} /> <Link to="/login" className="text-coral hover:underline">Log in</Link> to search and filter chefs. Browsing is free for everyone.
          </p>
        )}
        {filtering && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-ink-soft font-semibold">Filters:</span>
            {q && <button onClick={() => setParam("q", "")} className="rounded-full bg-secondary px-3 h-8 font-bold text-ink" data-testid="clear-q">“{q}” ×</button>}
            {city && <button onClick={() => setParam("city", "")} className="rounded-full bg-secondary px-3 h-8 font-bold text-ink" data-testid="clear-city">{city} ×</button>}
            {specialty && <button onClick={() => setParam("specialty", "")} className="rounded-full bg-secondary px-3 h-8 font-bold text-ink" data-testid="clear-specialty">{specialty} ×</button>}
            <button onClick={() => setParams(new URLSearchParams(sort !== "top" ? { sort } : {}))} className="text-coral font-bold ml-1" data-testid="clear-all-filters">Clear all</button>
          </div>
        )}
      </div>

      {!filtering && <FeaturedRow featured={featured} />}

      <section className="mt-10">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-heading text-2xl sm:text-3xl font-black text-ink">{filtering ? "Matching chefs" : "All chefs"}</h2>
          {!loading && <p className="text-sm font-bold text-ink-soft" data-testid="chefs-total">{total} {total === 1 ? "chef" : "chefs"}</p>}
        </div>
        {loading ? (
          <div className="mt-5 grid gap-4 grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-72 rounded-3xl skeleton" />)}</div>
        ) : gated ? (
          <div className="mt-5 rounded-3xl bg-white border border-border p-10 text-center" data-testid="chefs-gated">
            <Lock className="mx-auto text-coral mb-3" size={28} />
            <h3 className="font-heading text-xl font-black text-ink">Log in to search chefs</h3>
            <p className="text-ink-soft mt-1">Registered members can search by name, city, specialty or dish.</p>
            <Link to="/login" className="inline-flex mt-5 h-12 px-6 items-center rounded-full bg-coral text-white font-bold">Log in</Link>
          </div>
        ) : chefs.length === 0 ? (
          <div className="mt-5 rounded-3xl bg-white border border-border p-10 text-center" data-testid="chefs-empty">
            <h3 className="font-heading text-xl font-black text-ink">No chefs match yet</h3>
            <p className="text-ink-soft mt-1">Try another city or specialty — or be the first chef here.</p>
          </div>
        ) : (
          <>
            <div className="mt-5 grid gap-4 grid-cols-2 lg:grid-cols-4" data-testid="chefs-grid">
              {chefs.map((c) => <ChefCard key={c.id} chef={c} />)}
            </div>
            {hasMore && (
              <div className="mt-8 text-center">
                <button onClick={loadMore} data-testid="chefs-load-more" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white border border-border hover:border-coral font-bold text-ink transition-colors">
                  <Loader2 size={16} className="hidden" /> Load more chefs
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Become a chef CTA */}
      <section className="mt-14 rounded-[2rem] bg-ink text-white p-8 sm:p-12 relative overflow-hidden" data-testid="become-chef-cta">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-coral/20 blur-3xl" aria-hidden />
        <div className="relative max-w-2xl">
          <h2 className="font-heading text-2xl sm:text-3xl font-black">Cook something people remember?</h2>
          <p className="text-white/70 mt-2">Create your Chef Profile, publish original recipes with their stories, and build a following on Flavoria.</p>
          <Link to="/kitchen" data-testid="become-chef-button" className="inline-flex items-center gap-2 mt-6 h-12 px-6 rounded-full bg-coral hover:bg-coral-hover text-white font-bold transition-colors"><ChefHat size={18} /> Become a Chef</Link>
        </div>
      </section>
    </div>
  );
}
